import { FC, useEffect, useRef } from 'react';
import { translate } from '../../i18n';
import { AddedCardIcon, BlockedCardIcon } from './icons';
import { PaymentFailurePanel } from '../PaymentFailurePanel';
import { cardStatusModalStyles } from './styles';

export type CardStatusModalVariant = 'invalid' | 'expired' | 'success' | 'paymentError';

export interface CardStatusModalProps {
  variant: CardStatusModalVariant;
  onClose?: () => void;
  /** Texto debajo del título (p. ej. el detalle del error de pago). */
  message?: string;
  testID?: string;
}

const VARIANT_TITLE_KEY: Record<CardStatusModalVariant, string> = {
  invalid: 'paymentsUI.cardStatusModal.invalidTitle',
  expired: 'paymentsUI.cardStatusModal.expiredTitle',
  success: 'paymentsUI.cardStatusModal.successTitle',
  paymentError: 'paymentsUI.cardStatusModal.paymentErrorTitle',
};

/**
 * CardStatusModal — Modal de resultado al agregar una tarjeta
 * ("Tarjeta inválida" / "Tarjeta expirada" / "Tarjeta agregada con éxito").
 *
 * Solo maqueta el diseño: se muestra según la respuesta de un endpoint que
 * todavía no está implementado. Sin lógica de disparo/validación acá.
 */
export const CardStatusModal: FC<CardStatusModalProps> = ({
  variant,
  onClose,
  message,
  testID = 'card-status-modal',
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [variant, message]);

  if (variant === 'paymentError') {
    const detail =
      message && message.trim().length > 0
        ? message
        : translate('paymentsUI.paymentFailure.paymentDetail');
    const panelId = `${testID}-panel`;
    return (
      <div style={cardStatusModalStyles.overlay} data-testid={testID}>
        <div
          style={cardStatusModalStyles.sheet}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${panelId}-title`}
          aria-describedby={`${panelId}-detail`}
        >
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            data-testid={`${testID}-close`}
            aria-label={translate('paymentsUI.cardStatusModal.closeAriaLabel')}
            style={cardStatusModalStyles.closeButton}
          >
            <img
              src={`${import.meta.env.BASE_URL}assets/card-status/close-button.svg`}
              alt=""
              width="40"
              height="40"
            />
          </button>
          <PaymentFailurePanel
            variant="paymentDeclined"
            detail={detail}
            onAction={() => onClose?.()}
            testID={`${testID}-panel`}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={cardStatusModalStyles.overlay} data-testid={testID}>
      <div style={cardStatusModalStyles.sheet} role="dialog" aria-modal="true">
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          data-testid={`${testID}-close`}
          aria-label={translate('paymentsUI.cardStatusModal.closeAriaLabel')}
          style={cardStatusModalStyles.closeButton}
        >
          <img src={`${import.meta.env.BASE_URL}assets/card-status/close-button.svg`} alt="" width="40" height="40" />
        </button>

        <div style={cardStatusModalStyles.iconWrapper}>
          {variant === 'success' ? <AddedCardIcon /> : <BlockedCardIcon />}
        </div>

        <h2 style={cardStatusModalStyles.title}>{translate(VARIANT_TITLE_KEY[variant])}</h2>
        {message && <p style={cardStatusModalStyles.message}>{message}</p>}

        <button
          type="button"
          onClick={onClose}
          data-testid={`${testID}-done`}
          style={cardStatusModalStyles.doneButton}
        >
          {translate('paymentsUI.cardStatusModal.doneButton')}
        </button>
      </div>
    </div>
  );
};
